// /vms/export — 帳號 VMS 證據匯出（ls-loop MV.5，docs/mycam/mv-spec.md ④）。
// 選攝影機（≤ 4）+ 時段（≤ 30 分鐘）+ 原因（選填）+ 重新輸入密碼 → 瀏覽器取影片（雲端 / SD / 手機本機）轉 MP4、
// 算雜湊、組 manifest → 雲端簽章（驗密碼 + 記操作紀錄）→ 下載 ZIP。密碼錯：已取好的影片留著，重輸密碼只重簽。
// 驗證：/vms/verify（不用登入、檔案不上傳）。

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { apiFetch, ApiError } from '../../lib/api';
import { sharedZone } from '../../lib/camTz';
import { formatTimestampUs } from '../../lib/format';
import { isPhoneCam } from '../../lib/phoneCam';
import { queryDayRanges } from '../../lib/recPlayback/sdcardQuery';
import {
  buildManifest,
  collectFiles,
  currentUserId,
  EXPORT_MAX_CAMS,
  listCloudClips,
  maskEmail,
  newExportId,
  packZip,
  signManifest,
  type BuiltFiles,
  type ExportProgress,
} from '../../lib/vms/evidence';
import { fetchMyEvents, type MyEvent } from '../../lib/vms/myEvents';
import { buildTimeline } from '../../lib/vms/timeline';
import { cameraClass, deviceName } from '../devices/deviceModel';
import { useDeviceList } from '../devices/useDeviceList';
import './vms.css';

const DURATIONS_MIN = [1, 5, 10, 15, 30];

type Stage = 'idle' | 'checking' | 'fetching' | 'signing' | 'packing' | 'done' | 'error';

/** <input type=datetime-local> 的值（瀏覽器時區）↔ µs */
function toLocalInput(us: number): string {
  const d = new Date(us / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
const fromLocalInput = (s: string) => new Date(s).getTime() * 1000;

async function timelineOf(deviceId: string, fromUs: number, toUs: number) {
  const [cloud, local] = await Promise.all([
    listCloudClips(deviceId, fromUs, toUs)
      .then((cs) => cs.map((c) => ({ fromUs: c.clip_start_ts, toUs: c.clip_end_ts })))
      .catch(() => []),
    queryDayRanges(deviceId, fromUs, toUs).catch(() => ({ state: 'UNREACHABLE' as const, items: [] })),
  ]);
  return buildTimeline({
    window: { fromUs, toUs },
    cloud,
    local: (local.items ?? []).map((x) => ({ fromUs: x.start_us, toUs: x.end_us })),
    localState: local.state === 'UNREACHABLE' ? 'unreachable' : 'ok',
  });
}

async function eventsOf(devices: string[], fromUs: number, toUs: number): Promise<MyEvent[]> {
  const out: MyEvent[] = [];
  let cursor = '';
  for (let i = 0; i < 10; i++) {
    const p = await fetchMyEvents({ fromUs, toUs, devices, cursor, limit: 200 });
    out.push(...p.items);
    if (!p.next_cursor) break;
    cursor = p.next_cursor;
  }
  return out.sort((a, b) => a.event_ts - b.event_ts);
}

export default function ExportPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const { devices } = useDeviceList();
  const cameras = useMemo(() => (devices ?? []).filter((d) => cameraClass(d) === 'camera'), [devices]);

  const [selected, setSelected] = useState<string[]>([]);
  const [start, setStart] = useState(() => {
    const t0 = Number(params.get('t'));
    return toLocalInput(Number.isFinite(t0) && t0 > 0 ? t0 : (Date.now() - 10 * 60_000) * 1000);
  });
  const [minutes, setMinutes] = useState(5);
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [stage, setStage] = useState<Stage>('idle');
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ url: string; name: string; files: number; size: number; skipped: number; failed: number } | null>(null);
  const built = useRef<{ key: string; files: BuiltFiles; events: MyEvent[] } | null>(null);

  useEffect(() => {
    const ids = (params.get('ids') ?? '').split(',').filter(Boolean);
    if (ids.length && cameras.length) setSelected(ids.filter((id) => cameras.some((c) => c.device_id === id)).slice(0, EXPORT_MAX_CAMS));
  }, [params, cameras]);
  useEffect(() => () => {
    if (result) URL.revokeObjectURL(result.url);
  }, [result]);

  const fromUs = fromLocalInput(start);
  const toUs = fromUs + minutes * 60_000_000;
  const key = `${selected.join(',')}|${fromUs}|${toUs}`;
  const busy = stage === 'checking' || stage === 'fetching' || stage === 'signing' || stage === 'packing';
  const zone = sharedZone(selected);

  const toggle = (id: string) =>
    setSelected((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : xs.length >= EXPORT_MAX_CAMS ? xs : [...xs, id]));

  const run = async () => {
    setError(null);
    if (result) {
      URL.revokeObjectURL(result.url);
      setResult(null);
    }
    const uid = currentUserId();
    if (!uid) {
      setError(t('common.networkError'));
      return;
    }
    try {
      // 1–2. 取影片（同一組攝影機 / 時段已經取過 → 沿用，例如上次密碼打錯）
      if (!built.current || built.current.key !== key) {
        built.current = null;
        setStage('checking');
        const cams = await Promise.all(
          selected.map(async (id) => {
            const d = cameras.find((c) => c.device_id === id)!;
            return { deviceId: id, name: deviceName(d), phone: isPhoneCam(d.model), spans: await timelineOf(id, fromUs, toUs) };
          })
        );
        setStage('fetching');
        const files = await collectFiles(cams, fromUs, toUs, setProgress);
        if (files.files.length === 0) {
          setStage('error');
          setError(t('vmsExport.nothing'));
          return;
        }
        const events = await eventsOf(selected, fromUs, toUs).catch(() => []);
        built.current = { key, files, events };
      }
      const { files, events } = built.current;
      // 3–4. manifest → 簽章（驗密碼）
      setStage('signing');
      const eventsJson = JSON.stringify(events, null, 2);
      const exportId = newExportId();
      const { manifest, bytes } = await buildManifest({
        files: files.files,
        eventsJson,
        eventsCount: events.length,
        exportId,
        userId: uid,
        account: maskEmail(user?.email ?? (await apiFetch<{ email?: string }>('/v3/me').then((r) => r.data?.email).catch(() => undefined))),
        reason: reason.trim(),
        window: { from_us: fromUs, to_us: toUs },
        timeZone: zone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      const sig = await signManifest(bytes, password);
      setPassword('');
      // 5. ZIP → 下載
      setStage('packing');
      const zip = await packZip(bytes, sig, files.files, manifest, eventsJson);
      const url = URL.createObjectURL(zip);
      const name = `${exportId}.zip`;
      setResult({ url, name, files: files.files.length, size: zip.size, skipped: files.skipped, failed: files.failed });
      setStage('done');
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
    } catch (e) {
      setStage('error');
      if (e instanceof ApiError && e.code === 'WRONG_PASSWORD') setError(t('vmsExport.wrongPassword'));
      else if (e instanceof ApiError && e.code === 'PASSWORD_REQUIRED') setError(t('vmsExport.passwordRequired'));
      else if (e instanceof ApiError && e.status === 429) setError(t('vmsExport.tooMany'));
      else setError(t('vmsExport.failed'));
    }
  };

  const stageText =
    stage === 'checking' ? t('vmsExport.stage_checking')
      : stage === 'fetching' ? t('vmsExport.stage_fetching', { done: progress?.done ?? 0, total: progress?.total ?? 0, name: progress?.current ?? '' })
      : stage === 'signing' ? t('vmsExport.stage_signing')
      : stage === 'packing' ? t('vmsExport.stage_packing')
      : '';

  return (
    <section className="vms-ex">
      <header className="vms-ec-head">
        <h1 style={{ marginTop: 0 }}>{t('vmsExport.title')}</h1>
        <Link className="btn-ghost" to="/vms/verify">{t('vmsVerify.title')} →</Link>
      </header>
      <p className="text-secondary vms-ec-sub">{t('vmsExport.subtitle')}</p>

      <form
        className="vms-ec-filters card"
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
      >
        <div className="vms-ec-row">
          <span className="vms-ec-row-label text-secondary">{t('vmsExport.cameras', { max: EXPORT_MAX_CAMS })}</span>
          {cameras.map((d) => (
            <button type="button" key={d.device_id} className={`evt-chip${selected.includes(d.device_id) ? ' active' : ''}`} aria-pressed={selected.includes(d.device_id)}
              onClick={() => toggle(d.device_id)} disabled={busy} data-testid={`ex-cam-${d.device_id}`}>
              {deviceName(d)}
            </button>
          ))}
        </div>
        <div className="vms-ec-row">
          <label className="vms-ec-row-label text-secondary" htmlFor="ex-start">{t('vmsExport.start')}</label>
          <input id="ex-start" className="evt-date" type="datetime-local" step={1} value={start} onChange={(e) => e.target.value && setStart(e.target.value)} disabled={busy} data-testid="ex-start" />
          <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} aria-label={t('vmsExport.length')} disabled={busy} data-testid="ex-minutes">
            {DURATIONS_MIN.map((m) => (
              <option key={m} value={m}>{t('vmsExport.minutes', { count: m })}</option>
            ))}
          </select>
          <span className="text-secondary vms-lb-msg">
            {formatTimestampUs(fromUs, { timeZone: zone })} – {formatTimestampUs(toUs, { year: undefined, month: undefined, day: undefined, timeZone: zone })}
            {zone ? '' : ` · ${t('vmsExport.browserTz')}`}
          </span>
        </div>
        <div className="vms-ec-row">
          <label className="vms-ec-row-label text-secondary" htmlFor="ex-reason">{t('vmsExport.reason')}</label>
          <input id="ex-reason" style={{ flex: '1 1 16em' }} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('vmsExport.reasonPlaceholder')} disabled={busy} data-testid="ex-reason" />
        </div>
        <div className="vms-ec-row">
          <label className="vms-ec-row-label text-secondary" htmlFor="ex-pw">{t('vmsExport.password')}</label>
          <input id="ex-pw" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} data-testid="ex-password" />
          <button type="submit" className="btn-primary" disabled={busy || selected.length === 0 || !password || devices === null} data-testid="ex-run">
            {built.current?.key === key && stage === 'error' ? t('vmsExport.retrySign') : t('vmsExport.run')}
          </button>
        </div>
        <p className="text-secondary vms-lb-msg" style={{ margin: 0 }}>{t('vmsExport.passwordHint')}</p>
      </form>

      {busy && <p role="status" data-testid="ex-stage">{stageText}</p>}
      {error && <p className="text-danger" role="alert" data-testid="ex-error">{error}</p>}
      {result && (
        <div className="card" data-testid="ex-done">
          <p style={{ marginTop: 0 }}>
            {t('vmsExport.done', { files: result.files, mb: (result.size / 1e6).toFixed(1) })}
            {result.skipped ? ` ${t('vmsExport.skipped', { count: result.skipped })}` : ''}
            {result.failed ? ` ${t('vmsExport.failedPieces', { count: result.failed })}` : ''}
          </p>
          <a className="btn-primary" href={result.url} download={result.name} data-testid="ex-download">
            {t('vmsExport.download', { name: result.name })}
          </a>
          <p className="text-secondary vms-lb-msg">{t('vmsExport.verifyHint')}</p>
        </div>
      )}
    </section>
  );
}
